import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Camera, Upload, ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";
import { PasswordGate } from "@/components/PasswordGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function PhotosPage() {
  const [auth, setAuth] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploader, setUploader] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/photos");
      setPhotos(data);
    } catch {
      /* gallery stays empty */
    }
  }, []);

  useEffect(() => {
    api.get("/auth/site/me")
      .then(() => {
        setAuth(true);
        load();
        api.get("/auth/admin/me").then(() => setIsAdmin(true)).catch(() => {});
      })
      .catch(() => setAuth(false));
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file || busy) {
      if (!file) toast.error("Choose a photo first.");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("caption", caption.trim());
      form.append("uploader", uploader.trim());
      const { data } = await api.post("/photos", form);
      setPhotos((prev) => [data, ...prev]);
      setFile(null);
      setCaption("");
      setUploader("");
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Photo added to the gallery. Thank you!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const removePhoto = async (id) => {
    try {
      await api.delete(`/admin/photos/${id}`);
      setPhotos((prev) => prev.filter((p) => p.id !== id));
      toast.success("Photo removed.");
    } catch {
      toast.error("Could not remove photo.");
    }
  };

  if (auth === null) {
    return <div className="min-h-screen bg-[#050505]" data-testid="photos-loading" />;
  }

  if (!auth) {
    return (
      <PasswordGate
        overline="Strictly confidential"
        title={<>The <em className="text-gold">gallery</em>.</>}
        subtitle="Enter the party password to share and browse photos from the night."
        testIdPrefix="photos"
        onSubmit={async (password) => {
          await api.post("/auth/site", { password });
          setAuth(true);
          load();
          api.get("/auth/admin/me").then(() => setIsAdmin(true)).catch(() => {});
        }}
      />
    );
  }

  return (
    <main className="min-h-screen bg-[#050505] px-6 py-16 md:px-16 lg:px-24" data-testid="photos-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
        <div className="mb-6 flex items-center gap-6">
          <span className="font-display text-sm italic text-gold">After the party</span>
          <span className="gold-rule flex-1" aria-hidden="true" />
        </div>
        <div className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <h1 className="font-display text-4xl font-light tracking-tight text-[#F5F5F0] md:text-6xl">
            Share the <em className="text-gold">night</em>
          </h1>
          <p className="max-w-md text-sm leading-relaxed text-[#A1A19A]">
            Once the confetti settles on 5 December, add your favourite photos from the
            party here for everyone to enjoy — the masks, the characters, the moment she walked in.
          </p>
        </div>

        <form onSubmit={submit} className="mb-16 border border-white/10 bg-[#0D0D0D] p-8 md:p-10" data-testid="photo-upload-form">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end">
            <div className="flex-1">
              <label htmlFor="photo-file" className="mb-3 block text-xs uppercase tracking-[0.25em] text-[#A1A19A]">
                Your photo
              </label>
              <Input
                id="photo-file"
                ref={fileRef}
                data-testid="photo-file-input"
                type="file"
                accept="image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="h-14 cursor-pointer rounded-none border-white/15 bg-white/5 px-4 pt-4 text-sm text-[#A1A19A] file:mr-4 file:border-0 file:bg-gold file:px-4 file:py-1.5 file:text-xs file:uppercase file:tracking-[0.2em] file:text-[#050505] focus-visible:ring-1 focus-visible:ring-gold"
              />
            </div>
            <div className="flex-1">
              <label htmlFor="photo-uploader" className="mb-3 block text-xs uppercase tracking-[0.25em] text-[#A1A19A]">
                Your name
              </label>
              <Input
                id="photo-uploader"
                data-testid="photo-uploader-input"
                value={uploader}
                onChange={(e) => setUploader(e.target.value)}
                placeholder="So everyone knows who took it"
                maxLength={80}
                className="h-14 rounded-none border-white/15 bg-white/5 px-5 text-base text-[#F5F5F0] placeholder:text-[#666666] focus-visible:ring-1 focus-visible:ring-gold"
              />
            </div>
            <div className="flex-1">
              <label htmlFor="photo-caption" className="mb-3 block text-xs uppercase tracking-[0.25em] text-[#A1A19A]">
                Caption (optional)
              </label>
              <Input
                id="photo-caption"
                data-testid="photo-caption-input"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Who's behind the mask?"
                maxLength={200}
                className="h-14 rounded-none border-white/15 bg-white/5 px-5 text-base text-[#F5F5F0] placeholder:text-[#666666] focus-visible:ring-1 focus-visible:ring-gold"
              />
            </div>
            <Button
              data-testid="photo-upload-button"
              type="submit"
              disabled={busy}
              className="group h-14 rounded-none bg-gold px-8 text-xs font-medium uppercase tracking-[0.25em] text-[#050505] transition-colors duration-300 hover:bg-gold-hover"
            >
              <Camera className="mr-3 h-4 w-4" strokeWidth={1.5} />
              {busy ? "Uploading…" : "Add photo"}
            </Button>
          </div>
        </form>

        {photos.length === 0 ? (
          <div className="border border-white/10 px-8 py-20 text-center" data-testid="photo-gallery-empty">
            <Upload className="mx-auto mb-6 h-6 w-6 text-gold/60" strokeWidth={1.25} />
            <p className="font-display text-2xl font-light italic text-[#A1A19A]">
              Nothing here yet — the night is still to come.
            </p>
            <p className="mt-3 text-sm text-[#666666]">Check back after the party on 5 December.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="photo-gallery">
            {photos.map((p, i) => (
              <motion.figure
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                className="group overflow-hidden border border-white/10 bg-[#0D0D0D]"
                data-testid={`photo-card-${p.id}`}
              >
                <img
                  src={`${API_BASE}/photos/${p.id}/file`}
                  alt={p.caption || "Party photo"}
                  loading="lazy"
                  className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <figcaption className="flex items-start justify-between gap-4 px-5 py-4">
                  <div>
                    {p.caption && <p className="text-sm italic text-[#A1A19A]">{p.caption}</p>}
                    {p.uploader && (
                      <p className={`text-xs uppercase tracking-[0.2em] text-gold ${p.caption ? "mt-2" : ""}`}>
                        {p.uploader}
                      </p>
                    )}
                    {!p.caption && !p.uploader && <p className="text-sm text-[#666666]">Party photo</p>}
                  </div>
                  {isAdmin && (
                    <button
                      data-testid={`photo-delete-${p.id}`}
                      onClick={() => removePhoto(p.id)}
                      className="mt-0.5 shrink-0 text-[#666666] transition-colors duration-200 hover:text-[#E07070]"
                      aria-label="Remove photo"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  )}
                </figcaption>
              </motion.figure>
            ))}
          </div>
        )}

        <p className="mt-12 text-xs text-[#666666]">
          <a href="/" data-testid="photos-back-link" className="inline-flex items-center gap-2 border-b border-white/15 pb-0.5 transition-colors duration-200 hover:text-gold">
            <ArrowLeft className="h-3 w-3" strokeWidth={1.5} />
            Back to the invitation
          </a>
        </p>
      </motion.div>
    </main>
  );
}
